import { Injectable, Signal, computed, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AmplitudeService } from '../analytics/amplitude.service';

/**
 * Amplitude Experiment flag that gates the internal-only Agents nav dropdown
 * and its agent-chat routes. Targeted by user email.
 */
export const AGENTS_NAV_FLAG_KEY = 'agents-nav-enabled';

/**
 * Amplitude Experiment flag that gates the internal-only Configuration nav
 * item and the `/cmi/config` page. Targeted by user email.
 */
export const CONFIGURATION_NAV_FLAG_KEY = 'configuration-nav-enabled';

/**
 * Amplitude Experiment flag that gates the entire `/pricing` experience — both
 * its route and its sidebar entry. Unlike the other flags above, this one ships
 * with a fail-safe fallback to `environment.features.pricing` so the experience
 * stays hidden when Amplitude has not registered the flag for the caller.
 *
 * The `pricing` env flag is `false` in dev/test/prod, so the experience stays
 * hidden in every deployed environment until Amplitude reports `'on'` for the
 * caller; QA uses Amplitude targeting to progressively roll it out
 * per-user/per-tenant without redeploying. Only local dev (environment.ts)
 * leaves the fallback `true`, because Amplitude is disabled there and the
 * fallback is the sole gate.
 *
 * Note: this visibility gate is independent of `environment.features.pricingApi`,
 * which only switches the page between the live `/pricing/*` API and MOCK_PRICING.
 */
export const PRICING_ENABLED_FLAG_KEY = 'pricing-enabled';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  private readonly amplitude = inject(AmplitudeService);

  /**
   * Synchronous, fail-closed check. When Amplitude is disabled for the
   * current environment (local dev without an API key) every flag returns
   * `true` so developers can see gated surfaces. Otherwise a flag is on
   * only when Amplitude reports the variant value as `'on'`.
   */
  isEnabled(key: string): boolean {
    if (!environment.amplitude.enabled) {
      return true;
    }
    return this.amplitude.getVariantValue(key) === 'on';
  }

  /**
   * Reactive variant of {@link isEnabled}. Re-computes whenever Amplitude
   * publishes a new variant set (after init or a user-scoped refetch).
   */
  enabled(key: string): Signal<boolean> {
    return computed(() => {
      // Establish a reactive dependency on Amplitude's variant version so
      // the computed re-evaluates after each fetch.
      this.amplitude.variantsVersion();
      return this.isEnabled(key);
    });
  }

  /**
   * Synchronous check that falls back to a static value when Amplitude
   * either isn't configured for the environment or has no variant registered
   * for the key. Used by the `pricing-enabled` flag so QA can target the
   * route per-user/per-tenant via Amplitude without redeploying, while
   * environments lacking an Amplitude variant still respect the env-flag
   * default (e.g. prod stays off until the API ships).
   *
   * Resolution order:
   *  1. Amplitude is disabled for the env → return `fallback`.
   *  2. Amplitude reports an explicit `'on'` → `true`.
   *  3. Amplitude reports an explicit `'off'` → `false`.
   *  4. Amplitude has no variant (`undefined`) → return `fallback`.
   */
  isEnabledWithFallback(key: string, fallback: boolean): boolean {
    if (!environment.amplitude.enabled) {
      return fallback;
    }
    const value = this.amplitude.getVariantValue(key);
    if (value === 'on') return true;
    if (value === 'off') return false;
    return fallback;
  }

  /**
   * Reactive variant of {@link isEnabledWithFallback}. Re-computes whenever
   * Amplitude publishes a new variant set.
   */
  enabledWithFallback(key: string, fallback: boolean): Signal<boolean> {
    return computed(() => {
      this.amplitude.variantsVersion();
      return this.isEnabledWithFallback(key, fallback);
    });
  }

  /** Await init + the most recent variant refetch before sync reads. */
  ready(): Promise<void> {
    return this.amplitude.whenVariantsReady();
  }

  /**
   * APP_INITIALIZER barrier — explicitly drives Amplitude initialization
   * before awaiting the first variant fetch so first-paint flag reads
   * are authoritative.
   *
   * Previously this was a thin alias over {@link ready}, which only
   * awaits ``initPromise`` if it already exists. When Angular ran this
   * initializer in parallel with a separate ``AmplitudeService.init()``
   * APP_INITIALIZER, ``load()`` could resolve before
   * ``AmplitudeService.init()`` had even reached the line that creates
   * ``initPromise`` (that happens only after ``runtimeSecrets.load()``
   * settles). On a cold boot the app would then render and run guards
   * with every flag still reading as ``undefined`` (i.e. off), which
   * could hide nav items or redirect entitled users away from gated
   * routes on first paint until a later re-render.
   *
   * Calling ``amplitude.init()`` here makes the boot sequence
   * deterministic: this initializer owns Amplitude bootstrap end-to-end
   * (the call is idempotent — see ``AmplitudeService.init``'s
   * ``initPromise`` guard).
   */
  async load(): Promise<void> {
    await this.amplitude.init();
    await this.ready();
  }
}
